<template>
  <div class="viewer" v-if="value">
    <div class="viewer-bar">
      <v-btn text dark @click="hidePreview"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <div class="ml-3">{{ title }}</div>
      <template v-if="isType == 'document' || isType == 'pdf'">
        <v-spacer /><v-icon class="ml-3 mr-1" color="white">mdi-eye-circle</v-icon> {{ $t('readonly') }}
      </template>
      <v-spacer />
      <v-btn v-if="!hideDownload" text dark :href="linkDownload"> {{ $t('download') }}<v-icon class="ml-2">mdi-download</v-icon> </v-btn>
    </div>
    <iframe v-if="isType == 'pdf'" :src="url" class="viewer-content" title="file-viewer" />
    <template v-else-if="isType == 'document'">
      <iframe
        v-if="limited <= 5000"
        :src="`https://view.officeapps.live.com/op/embed.aspx?src=${url}`"
        class="viewer-content"
        title="office-live-apps"
      />
      <div v-else :style="{ flex: 1 }" class="d-flex align-center justify-center text-center">
        <v-card class="pa-10" :style="{ 'max-width': 400 }">
          <svg-icon icon="note" class="primary--text mb-3" width="100" height="100" />
          <div class="mb-3">{{ $t('content.file.file_large') }}</div>
          <v-btn color="primary" class="btn-primary-custom" target="_blank" link :href="linkDownload">
            {{ $t('download') }}<v-icon class="ml-2">mdi-download</v-icon>
          </v-btn>
        </v-card>
      </div>
    </template>
    <div v-else-if="isType == 'video'" :style="{ flex: 1 }" class="d-flex align-center justify-center text-center">
      <video controls :style="{ width: '50vw' }">
        <source :src="url" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
    <div v-else-if="isType == 'audio'" :style="{ flex: 1 }" class="d-flex align-center justify-center text-center">
      <audio controls>
        <source :src="url" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
    </div>
    <div v-else-if="isType == 'image'" :style="{ flex: 1 }">
      <div
        :style="{
          height: '100%',
          'background-image': `url('${url}')`,
          'background-size': 'contain',
          'background-position': 'center center'
        }"
      ></div>
    </div>
    <div v-else :style="{ flex: 1 }" class="d-flex align-center justify-center text-center">
      <v-card class="pa-10" :style="{ 'max-width': 400 }">
        <svg-icon icon="note" class="primary--text mb-3" width="100" height="100" />
        <div class="mb-3">{{ $t('content.file.file_not_supported') }}</div>
        <v-btn color="primary" class="btn-primary-custom" target="_blank" link :href="linkDownload">
          {{ $t('download') }}<v-icon class="ml-2">mdi-download</v-icon>
        </v-btn>
      </v-card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FileViewer',
  model: {
    prop: 'value',
    event: 'click'
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    data: {
      type: Object,
      default: function() {
        return {};
      }
    },
    hideDownload: {
      type: Boolean,
      default: false
    },
    hideExtension: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    type() {
      return this.data?.extension.toLowerCase() ?? null;
    },
    title() {
      const { data, hideExtension } = this;

      if (!data) return null;

      const title = data.title || data.submissionName || data.fileName;
      if (!title) return null;

      const normalized = title.replace(/\\/g, '/');
      const baseName = normalized.substring(
        normalized.lastIndexOf('/') + 1,
        normalized.lastIndexOf('.') > 0 ? normalized.lastIndexOf('.') : undefined
      );

      const finalName = baseName || normalized;
      const ext = this.type ? '.' + this.type : '';
      return hideExtension ? finalName : `${finalName}${ext}`;
    },
    url() {
      if (this.data?.url) {
        return this.data?.url;
      } else if (this.data?.fileUrl) {
        return this.data?.fileUrl;
      } else {
        return null;
      }
      // return this.data?.url ?? null;
    },
    size() {
      return this.data?.size ?? null;
    },
    key() {
      return this.data?.key ?? '';
    },
    isType() {
      if (['mp4'].includes(this.type)) {
        return 'video';
      } else if (['mp3'].includes(this.type)) {
        return 'audio';
      } else if (['pptx', 'ppt', 'doc', 'docx', 'xls', 'xlsx'].includes(this.type)) {
        return 'document';
      } else if (['pdf'].includes(this.type)) {
        return 'pdf';
      } else if (['jpg', 'png', 'jpeg'].includes(this.type)) {
        return 'image';
      }
      return null;
    },
    limited() {
      const size = this.size.split(' ');
      const transform = { KB: 1, MB: 1000, GB: 10000 };
      return size[0] * transform[size[1]];
    },
    linkDownload() {
      return `${this.baseUrl}api/file/download?key=${this.key}`;
    }
  },
  data: () => ({
    baseUrl: process.env.VUE_APP_BASE_URL
  }),
  methods: {
    hidePreview() {
      this.$emit('onHide');
    }
  }
};
</script>

<style lang="scss" scoped>
.viewer {
  @extend %overlay;
  color: #fff;
  display: flex;
  flex-direction: column;
  z-index: 200;

  &-bar {
    display: flex;
    align-items: center;
    padding: 20px;
    background: rgba($color: #000000, $alpha: 0.5);
  }

  &-content {
    flex-basis: 100%;
    border: none;
  }
}
</style>
